import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Grid,
  Paper,
  Divider,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  MedicalServicesOutlined,
  DescriptionOutlined,
  VerifiedUserOutlined,
  EuroSymbolOutlined,
  AccessTimeOutlined,
  LocationOnOutlined,
  EventAvailableOutlined,
  CheckCircleOutline,
  HighlightOff,
  InfoOutlined,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import ReservedSlotRow from './ReservedSlotRow';

// ==========================================
// SOUS-COMPOSANT : EN-TÊTE DE LA MODALE
// ==========================================
const DialogHeader = ({ info, onClose }) => {
  const isActif = Boolean(info?.status);
  const isFinancialValidated = Boolean(info?.is_validated_financial);

  return (
    <DialogTitle sx={{ p: 3, bgcolor: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              bgcolor: '#EEF2FF',
              p: 1.5,
              borderRadius: 3,
              display: 'flex',
              color: '#4318FF',
            }}
          >
            <MedicalServicesOutlined fontSize="large" />
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: '#4318FF', fontWeight: 800, letterSpacing: 1.2, display: 'block' }}
            >
              DOSSIER DE COORDINATION & SOINS {info?.id_info_offre ? `(ID : ${info.id_info_offre})` : ''}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mt: 0.5 }}>
              {info?.titre_offre || 'Offre sans titre'}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{ bgcolor: '#F1F5F9', '&:hover': { bgcolor: '#E2E8F0' } }}
          aria-label="Fermer"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Badges de statut & méta-données globales */}
      <Stack direction="row" spacing={1.5} mt={2.5} flexWrap="wrap" useFlexGap>
        <Chip
          label={isActif ? '● Service Actif' : '○ Service Suspendu'}
          sx={{
            bgcolor: isActif ? '#ECFDF5' : '#FEF2F2',
            color: isActif ? '#059669' : '#DC2626',
            fontWeight: 700,
            borderRadius: '8px',
            border: `1px solid ${isActif ? '#A7F3D0' : '#FECACA'}`,
          }}
        />
        <Chip
          label={`Type : ${info?.type_offre ? info.type_offre.replace('_', ' ').toUpperCase() : 'N/A'}`}
          variant="outlined"
          sx={{ fontWeight: 600, color: '#64748B', borderColor: '#CBD5E1', borderRadius: '8px' }}
        />
        <Chip
          label={`Format : ${info?.format_offre ? info.format_offre.toUpperCase() : 'N/A'}`}
          sx={{ bgcolor: '#EEF2FF', color: '#4318FF', fontWeight: 700, borderRadius: '8px' }}
        />
        <Chip
          icon={isFinancialValidated ? <CheckCircleOutline /> : <HighlightOff />}
          label={isFinancialValidated ? 'Financier Validé' : 'Financier Non Validé'}
          sx={{
            bgcolor: isFinancialValidated ? '#F0FDF4' : '#FFFBEB',
            color: isFinancialValidated ? '#16A34A' : '#D97706',
            fontWeight: 700,
            borderRadius: '8px',
          }}
        />
      </Stack>
    </DialogTitle>
  );
};

// ==========================================
// SOUS-COMPOSANT : CARTE INTERVENANT
// ==========================================
const IntervenantCard = ({ intervenant, idIntervenant }) => {
  const photoUrl = intervenant?.photo_user
    ? `${process.env.REACT_APP_API_URL}/storage/workshop/${intervenant.photo_user}`
    : '';

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2}>
        Intervenant / Praticien Référent {idIntervenant ? `(ID: ${idIntervenant})` : ''}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar src={photoUrl} sx={{ width: 56, height: 56, borderRadius: 2.5, border: '2px solid #F1F5F9' }}>
          {intervenant?.prenom_user?.[0]}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
            {intervenant?.prenom_user} {intervenant?.nom_user}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4318FF', fontWeight: 700, fontSize: '0.75rem' }}>
            {intervenant?.specialite || intervenant?.title_user || 'Spécialité / Fonction non spécifiée'}
          </Typography>
          <Typography variant="caption" color="#64748B" display="block">
            {intervenant?.email_user}
          </Typography>
        </Box>
      </Stack>
      {intervenant?.description_user && (
        <>
          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748B', fontSize: '0.8rem' }}>
            &quot;{intervenant.description_user}&quot;
          </Typography>
        </>
      )}
    </Paper>
  );
};

// ==========================================
// COMPOSANT PRINCIPAL : OFFREDETAILDIALOG
// ==========================================
const OffreDetailDialog = ({ open, offre, onClose, onUpdateSlotStatus }) => {
  // Extraction sécurisée des données principales
  const info = offre?.info_offre;
  const intervenant = offre?.intervenant;
  const availabilityList = offre?.availability || [];

  // Parse sécurisé des jours off
  const joursOffList = useMemo(() => {
    if (!info?.jours_off) return [];
    try {
      return typeof info.jours_off === 'string' ? JSON.parse(info.jours_off) : info.jours_off;
    } catch (e) {
      console.error('Erreur de parsing de jours_off :', e);
      return [];
    }
  }, [info?.jours_off]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        zIndex: 3000,
        '& .MuiDialog-paper': {
          borderRadius: 4,
          bgcolor: '#F8FAFC',
          overflow: 'hidden',
          boxShadow: '0px 25px 50px -12px rgba(15, 23, 42, 0.25)',
        },
      }}
    >
      {offre && (
        <>
          <DialogHeader info={info} onClose={onClose} />

          <DialogContent sx={{ p: 3, bgcolor: '#F8FAFC' }}>
            <Grid container spacing={3}>
              {/* --- COLONNE GAUCHE : INTERVENANT, FINANCE & PARAMÈTRES --- */}
              <Grid item xs={12} md={5}>
                {/* 1. Intervenant */}
                <IntervenantCard intervenant={intervenant} idIntervenant={info?.id_intervenant} />

                {/* 2. Description */}
                {info?.description_offre && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={1} display="flex" alignItems="center" gap={1}>
                      <DescriptionOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Description
                    </Typography>
                    <Typography variant="body2" color="#4A5568">
                      {info.description_offre}
                    </Typography>
                  </Paper>
                )}

                {/* 3. Validation & Statuts */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2} display="flex" alignItems="center" gap={1}>
                    <VerifiedUserOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Validation & Statut
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Statut du service :</Typography>
                      <Chip
                        label={info?.status ? 'Actif' : 'Suspendu'}
                        size="small"
                        sx={{
                          bgcolor: info?.status ? '#ECFDF5' : '#FEF2F2',
                          color: info?.status ? '#059669' : '#DC2626',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Validation Financière :</Typography>
                      <Chip
                        icon={info?.is_validated_financial ? <CheckCircleOutline /> : <HighlightOff />}
                        label={info?.is_validated_financial ? 'Validé' : 'Non Validé'}
                        size="small"
                        sx={{
                          bgcolor: info?.is_validated_financial ? '#F0FDF4' : '#FFFBEB',
                          color: info?.is_validated_financial ? '#16A34A' : '#D97706',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  </Stack>
                </Paper>

                {/* 4. Tarification & Finances */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2} display="flex" alignItems="center" gap={1}>
                    <EuroSymbolOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Tarification & Finances
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Payant :</Typography>
                      <Chip
                        label={info?.is_paid_offre ? 'Oui' : 'Non'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: info?.is_paid_offre ? '#ECFDF5' : '#F1F5F9',
                          color: info?.is_paid_offre ? '#059669' : '#64748B',
                        }}
                      />
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Prix Standard :</Typography>
                      <Typography variant="body2" fontWeight={800} color="#0F172A">
                        {info?.prix_offre ? `${info.prix_offre} €` : '—'}
                      </Typography>
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Prix Spécial SOA :</Typography>
                      <Typography variant="body2" fontWeight={800} color="#4318FF">
                        {info?.prix_special_soa ? `${info.prix_special_soa} €` : '—'}
                      </Typography>
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Somme donnée client</Typography>
                      <Typography variant="body2" fontWeight={800} color="#0F172A">
                        {info?.somme_donnee_client ? `${info.somme_donnee_client} €` : '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* 5. Horaires & Paramètres */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2} display="flex" alignItems="center" gap={1}>
                    <AccessTimeOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Horaires & Paramètres
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Durée de la séance :</Typography>
                      <Typography variant="body2" fontWeight={800} color="#0F172A">
                        {info?.duree_offre} min {info?.duree_pause ? `(+${info.duree_pause}m pause)` : ''}
                      </Typography>
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box>
                      <Typography variant="body2" color="#64748B" mb={0.5}>Plages horaires :</Typography>
                      {info?.plage_horaire_matin_debut && (
                        <Typography variant="caption" fontWeight={700} color="#0F172A" display="block">
                          🌅 Matin : {info.plage_horaire_matin_debut.substring(0, 5)} - {info.plage_horaire_matin_fin?.substring(0, 5)}
                        </Typography>
                      )}
                      {info?.plage_horaire_aprem_debut && (
                        <Typography variant="caption" fontWeight={700} color="#0F172A" display="block">
                          🌆 Après-midi : {info.plage_horaire_aprem_debut.substring(0, 5)} - {info.plage_horaire_aprem_fin?.substring(0, 5)}
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Accessible participant :</Typography>
                      <Typography variant="caption" fontWeight={700} color={info?.is_accessible_participant ? '#16A34A' : '#DC2626'}>
                        {info?.is_accessible_participant ? 'Oui' : 'Non'}
                      </Typography>
                    </Box>
                    <Divider sx={{ borderStyle: 'dotted' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="#64748B">Déplacement possible :</Typography>
                      <Typography variant="caption" fontWeight={700} color={info?.is_deplacement_possible ? '#16A34A' : '#64748B'}>
                        {info?.is_deplacement_possible ? 'Oui' : 'Non'}
                      </Typography>
                    </Box>

                    {joursOffList.length > 0 && (
                      <>
                        <Divider sx={{ borderStyle: 'dotted' }} />
                        <Box>
                          <Typography variant="body2" color="#DC2626" fontWeight={700} mb={1}>
                            Jours de fermeture :
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {joursOffList.map((day) => (
                              <Chip
                                key={day}
                                label={day}
                                size="small"
                                sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 700, fontSize: '0.65rem' }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Paper>

                {/* 6. Localisation */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2} display="flex" alignItems="center" gap={1}>
                    <LocationOnOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Localisation & Lieux
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" color="#64748B">Type de lieu :</Typography>
                    <Chip label={info?.location_type || 'Non spécifié'} size="small" sx={{ bgcolor: '#F1F5F9', fontWeight: 600, color: '#0F172A' }} />
                  </Box>
                  <Divider sx={{ borderStyle: 'dotted', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={700} color="#0F172A">
                    {info?.address_ws || 'Adresse non renseignée'}
                  </Typography>
                  <Typography variant="caption" color="#64748B" display="block" mb={1.5}>
                    {info?.postal_code_ws || ''} {info?.city_ws || ''} {info?.country_ws ? `(${info.country_ws})` : ''}
                  </Typography>

                  <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="caption" color="#64748B"><b>Lat:</b> {info?.lat_ws || '0.0'}</Typography>
                    <Typography variant="caption" color="#64748B"><b>Long:</b> {info?.lng_ws || '0.0'}</Typography>
                  </Box>

                  {info?.location_note_ws && (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <InfoOutlined sx={{ fontSize: 16, color: '#64748B', mt: 0.2 }} />
                      <Typography variant="caption" color="#64748B" fontStyle="italic">
                        {info.location_note_ws}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              </Grid>

              {/* --- COLONNE DROITE : PLANNING ET RÉSERVATIONS PATIENTS --- */}
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" mb={2} display="flex" alignItems="center" gap={1}>
                    <EventAvailableOutlined sx={{ fontSize: 18, color: '#4318FF' }} /> Planning des Disponibilités & Réservations
                  </Typography>

                  {availabilityList.length > 0 ? (
                    <Stack spacing={2}>
                      {availabilityList.map((a) => (
                        <Paper
                          key={a.id_dispo}
                          elevation={0}
                          sx={{ borderRadius: 2.5, border: '1px solid #E2E8F0', overflow: 'hidden', bgcolor: '#F8FAFC' }}
                        >
                          <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
                            <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                              {a.titre_dispo || 'Disponibilité sans titre'}
                            </Typography>
                            <Typography variant="caption" color="#64748B">
                              Périodicité : Du {a.date_debut ? new Date(a.date_debut).toLocaleDateString() : 'N/A'} au{' '}
                              {a.date_fin ? new Date(a.date_fin).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Box>

                          <Accordion
                            elevation={0}
                            disableGutters
                            sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: '#4318FF' }} />}
                              sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                            >
                              <Typography variant="caption" fontWeight={800} color="#4318FF">
                                CRÉNEAUX RÉSERVÉS PAR LES PATIENTS ({a.reserved_slots?.length || 0})
                              </Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 2, maxHeight: 320, overflowY: 'auto', bgcolor: '#FFFFFF' }}>
                              <Stack spacing={1.5}>
                                {a.reserved_slots?.length > 0 ? (
                                  a.reserved_slots.map((slot) => (
                                    <ReservedSlotRow
                                      key={slot.id_reserved_slot}
                                      slot={slot}
                                      onUpdateSlotStatus={onUpdateSlotStatus}
                                    />
                                  ))
                                ) : (
                                  <Typography variant="caption" color="#64748B" sx={{ textAlign: 'center', py: 3, display: 'block' }}>
                                    Aucun créneau réservé pour cette période.
                                  </Typography>
                                )}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                      <Typography variant="body2" color="#64748B">
                        Aucun planning de disponibilité configuré pour cette offre.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          {/* --- PIED DE PAGE --- */}
          <DialogActions sx={{ p: 2.5, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0' }}>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                bgcolor: '#4318FF',
                color: '#FFF',
                fontWeight: 700,
                borderRadius: '10px',
                px: 4,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3311CC', boxShadow: 'none' },
              }}
            >
              Fermer le Dossier
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default OffreDetailDialog;